// Full-screen chart view (docs/design/DESIGN_SYSTEM.md → "Mobile" → charts).
// ChartFrame opens it from its Expand button (and from a tap on coarse
// pointers). The SAME chart element re-renders at full size inside a portal:
//   • 100 svh overlay on surface-0, safe-area padded, z-drawer; Escape, the
//     ≥ 44 px close button and focus trap / focus return like Drawer;
//   • a fixed readout bar ABOVE the plot shows the scrubbed point (touch-and-
//     hold or drag → values), so a thumb never covers the number it reveals.
//     The readout captures the chart's own Tooltip payload (same formatter,
//     same label) by swapping the Tooltip's `content`, so it always agrees
//     with the in-place tooltip;
//   • "Rotate for a wider view" with the rotate glyph, portrait phones only.

import { Children, cloneElement, isValidElement, useCallback, useEffect, useId, useMemo, useRef, useState, type ReactElement, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { RotateCw, X } from "lucide-react";
import { ResponsiveContainer, Tooltip, type TooltipPayloadEntry } from "recharts";

import { useHScrollFade } from "@/app/console/useHScroll";
import { usePhone, usePointerCoarse, usePortrait } from "@/app/console/useViewport";

export interface ChartFullscreenProps {
  open: boolean;
  onClose: () => void;
  title: string;
  /** The recharts chart element (the same one ChartFrame renders in place). */
  children: ReactElement;
}

interface ReadoutItem {
  key: string;
  name: ReactNode;
  value: ReactNode;
  color?: string;
}
interface Readout {
  label: ReactNode;
  items: ReadoutItem[];
}

type Formatter = (value: unknown, name: unknown, item: TooltipPayloadEntry, index: number, payload: ReadonlyArray<TooltipPayloadEntry>) => ReactNode | [ReactNode, ReactNode];
type LabelFormatter = (label: unknown, payload: ReadonlyArray<TooltipPayloadEntry>) => ReactNode;

const FOCUSABLE = 'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])';

/** Rendered by recharts as the Tooltip's `content`: paints nothing, reports the active point. */
function ReadoutCapture({
  active,
  payload,
  label,
  formatter,
  labelFormatter,
  onChange,
}: {
  active?: boolean;
  payload?: ReadonlyArray<TooltipPayloadEntry>;
  label?: unknown;
  formatter?: Formatter;
  labelFormatter?: LabelFormatter;
  onChange: (r: Readout | null) => void;
}) {
  useEffect(() => {
    if (!active || !payload || payload.length === 0) {
      onChange(null);
      return;
    }
    const items: ReadoutItem[] = [];
    payload.forEach((p, i) => {
      // Band scaffolding (QuantileBandChart `_outerBase`…) and tooltipType="none" stay hidden.
      if (p.type === "none") return;
      if (typeof p.dataKey === "string" && p.dataKey.startsWith("_")) return;
      let name: ReactNode = p.name as ReactNode;
      let value: ReactNode = p.value as ReactNode;
      if (formatter) {
        const r = formatter(p.value, p.name, p, i, payload);
        if (Array.isArray(r)) {
          value = r[0];
          if (r[1] != null && r[1] !== "") name = r[1];
        } else if (r != null) {
          value = r;
        }
      } else if (typeof p.value === "number") {
        value = p.value.toLocaleString("en-IN", { maximumFractionDigits: 2 });
      }
      items.push({ key: `${String(p.dataKey ?? i)}-${i}`, name, value, color: p.color ?? p.stroke ?? p.fill });
    });
    onChange({ label: labelFormatter ? labelFormatter(label, payload) : (label as ReactNode), items });
  }, [active, payload, label, formatter, labelFormatter, onChange]);
  return null;
}

export function ChartFullscreen({ open, onClose, title, children }: ChartFullscreenProps) {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  const [readout, setReadout] = useState<Readout | null>(null);
  const phone = usePhone();
  const portrait = usePortrait();
  const coarse = usePointerCoarse();
  // Portrait phones: the chips wrap onto a second row so every value stays on
  // screen; otherwise the bar scrolls sideways behind an edge fade.
  const wrapReadout = phone && portrait;
  const readoutRef = useRef<HTMLDivElement>(null);
  useHScrollFade(readoutRef, open && !wrapReadout);

  // Stable callback → the cloned chart element below keeps its identity across
  // readout updates, so a pointer move re-renders the chips, not the chart.
  const onReadout = useCallback((r: Readout | null) => setReadout(r), []);

  const chart = useMemo(() => {
    const kids = Children.toArray(children.props.children as ReactNode).map((k) => {
      if (!isValidElement(k) || k.type !== Tooltip) return k;
      const tp = k.props as { formatter?: Formatter; labelFormatter?: LabelFormatter };
      return cloneElement(k as ReactElement<Record<string, unknown>>, {
        content: <ReadoutCapture formatter={tp.formatter} labelFormatter={tp.labelFormatter} onChange={onReadout} />,
        wrapperStyle: { visibility: "hidden" },
      });
    });
    return cloneElement(children, undefined, ...kids);
  }, [children, onReadout]);

  // Escape, focus trap, focus return, scroll lock — the Drawer contract.
  useEffect(() => {
    if (!open) return;
    openerRef.current = document.activeElement as HTMLElement | null;
    const focusables = () => Array.from(panelRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== "Tab") return;
      const els = focusables();
      if (els.length === 0) {
        e.preventDefault();
        panelRef.current?.focus();
        return;
      }
      const first = els[0]!;
      const last = els[els.length - 1]!;
      const active = document.activeElement;
      const inside = panelRef.current?.contains(active) ?? false;
      if (e.shiftKey && (active === first || !inside)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (active === last || !inside)) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey, true);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const id = window.setTimeout(() => panelRef.current?.focus(), 20);
    const opener = openerRef.current;
    return () => {
      document.removeEventListener("keydown", onKey, true);
      document.body.style.overflow = prev;
      window.clearTimeout(id);
      setReadout(null);
      if (opener && opener.isConnected && opener !== document.body) opener.focus();
    };
  }, [open, onClose]);

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <div
      ref={panelRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      tabIndex={-1}
      data-chart-fullscreen=""
      className="fixed inset-0 z-drawer flex h-[100svh] flex-col bg-surface-0 text-text-1 outline-none"
      style={{
        paddingTop: "var(--pb-safe-top)",
        paddingBottom: "var(--pb-safe-bottom)",
        paddingLeft: "var(--pb-safe-left)",
        paddingRight: "var(--pb-safe-right)",
      }}
    >
      <header className="flex h-12 shrink-0 items-center gap-2 border-b border-line px-2 sm:px-3">
        <h2 id={titleId} className="min-w-0 flex-1 truncate pl-1 text-[15px] font-semibold leading-[22px] text-text-1">
          {title}
        </h2>
        {phone && portrait && (
          <span className="inline-flex shrink-0 items-center gap-1.5 text-[11px] leading-4 text-text-3" aria-live="polite">
            <RotateCw className="h-3.5 w-3.5" aria-hidden="true" />
            Rotate for a wider view
          </span>
        )}
        <button
          type="button"
          onClick={onClose}
          aria-label="Close full-screen chart"
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-control text-text-3 transition-colors duration-fast hover:bg-white/[0.06] hover:text-text-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/60 coarse:h-11 coarse:w-11"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </header>

      {/* Readout: the scrubbed point, kept above the plot and off the thumb. */}
      <div
        ref={readoutRef}
        className={`flex min-h-[44px] shrink-0 items-center gap-2 border-b border-line bg-surface-1 px-3 py-1.5 ${wrapReadout ? "flex-wrap" : "pb-hscroll"}`}
        aria-live="polite"
        aria-atomic="true"
      >
        {readout && readout.items.length > 0 ? (
          <>
            {readout.label != null && readout.label !== "" && (
              <span className="shrink-0 whitespace-nowrap font-mono text-xs tabular-nums text-text-2">{readout.label}</span>
            )}
            {readout.items.map((it) => (
              <span key={it.key} className="inline-flex h-7 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md border border-line bg-surface-2/60 px-2">
                {it.color && <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: it.color }} aria-hidden="true" />}
                <span className="text-[11px] leading-4 text-text-3">{it.name}</span>
                <span className="font-mono text-[13px] font-medium tabular-nums text-text-1">{it.value}</span>
              </span>
            ))}
          </>
        ) : (
          <span className="text-xs text-text-3">{coarse ? "Touch and hold, then drag across the chart to read values" : "Hover over the chart to read values"}</span>
        )}
      </div>

      <div className="min-h-0 flex-1 p-2 sm:p-4" data-chart-frame="">
        <ResponsiveContainer width="100%" height="100%" debounce={60}>
          {chart}
        </ResponsiveContainer>
      </div>
    </div>,
    document.body,
  );
}
